import React from 'react';
import { Images } from 'utils';
import { SimpleTooltip } from 'components';
import { useFormBuilder, formPageEnums } from '../formContext';
import { formModelEnums } from '../../constants';
import { isContinuousSections } from '../section';
import pageSettings from '../../../../assets/images/icons/pageSettings.svg';

export const FormSettingsProperties = () => {
    const { formList, setFormList, setActiveFormPage } = useFormBuilder();

    const isClinicalNotes = formList?.model === formModelEnums.CLINICALNOTES;

    const handleChange = (field, value) => {
        setFormList({
            ...formList,
            [field]: value,
        });
    };

    const handleFormSettingsChange = (field, value) => {
        setFormList({
            ...formList,
            formSettings: {
                ...formList?.formSettings,
                [field]: value,
            },
        });
    };

    return (
        <div className="properties-wrapper form-settings-properties">
            <div className="form-settings-panel-header">
               <div>
                <div className="flex-align-center" style={{ gap: 8 }}>
                    <img src={Images.pageSettings} alt="settings" style={{ width: 18, height: 18 }} />
                    <h3 className="form-settings-panel-title">Form Settings</h3>
                </div>
                <p className="form-settings-panel-subtitle">Template-level configuration</p>
               </div>
                <button className="reset-btn-by-default" onClick={() => setActiveFormPage(null)}>
                    <img src={Images.closeSingleVector} alt="close" style={{ width: 18, height: 18 }} />
                </button>
            </div>

            {isClinicalNotes && (
                <>
                    <div className="form-settings-section">
                        <div className="form-settings-lock-row">
                            <div className="form-settings-lock-info">
                                <div className="flex-align-center" style={{ gap: 6 }}>
                                    <img src={Images.lockGray} alt="lockIcon" />
                                    <span className="form-settings-lock-title">Lock note after submit</span>
                                    <SimpleTooltip
                                        title="When enabled, submitted notes will be locked and cannot be edited without supervisor approval"
                                        placement="top"
                                        arrow
                                    >
                                        <img src={Images.toolInfoIcon} alt="info"
                                             style={{ width: 14, height: 14, cursor: 'pointer' }} />
                                    </SimpleTooltip>
                                </div>
                            </div>
                            <label className="toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={!!formList?.formSettings?.lockNoteAfterSubmit}
                                    onChange={(e) => handleFormSettingsChange('lockNoteAfterSubmit', e.target.checked)}
                                />
                                <span className="slider"></span>
                            </label>
                        </div>
                        <p className="form-settings-lock-desc">
                            {formList?.formSettings?.lockNoteAfterSubmit
                                ? 'Submitted notes will be locked. Supervisor approval is required to make changes.'
                                : 'Submitted notes remain editable by their creators. No supervisor approval is required to change them.'}
                        </p>
                    </div>

                    <div className="form-settings-section" style={{ marginTop: 0 }}>
                        <div className="form-settings-lock-row">
                            <div className="form-settings-lock-info">
                                <div className="flex-align-center" style={{ gap: 6 }}>
                                    <img src={Images.lockGray} alt="lockIcon" />
                                    <span className="form-settings-lock-title">Require approval before completing</span>
                                    <SimpleTooltip
                                        title="When enabled, submitted notes go to IN_REVIEW status and require supervisor approval to be marked as COMPLETED"
                                        placement="top"
                                        arrow
                                    >
                                        <img src={Images.toolInfoIcon} alt="info"
                                             style={{ width: 14, height: 14, cursor: 'pointer' }} />
                                    </SimpleTooltip>
                                </div>
                            </div>
                            <label className="toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={!!formList?.formSettings?.needApproved}
                                    onChange={(e) => handleFormSettingsChange('needApproved', e.target.checked)}
                                />
                                <span className="slider"></span>
                            </label>
                        </div>
                        <p className="form-settings-lock-desc">
                            {formList?.formSettings?.needApproved
                                ? 'Submitted notes will go to In Review and require supervisor approval before being marked as Completed.'
                                : 'Submitted notes will be marked as Completed immediately. No supervisor approval is required.'}
                        </p>
                    </div>
                    <div className="properties-divider" />
                </>
            )}

            <div className="form-settings-section">
                <div className="form-settings-lock-row">
                    <div className="form-settings-lock-info">
                        <div className="flex-align-center section-settings-title-row">
                            <span className="form-settings-lock-title">Continuous sections</span>
                            <SimpleTooltip
                                title="When on, sections follow one another on the same page. When off, every section starts on a new page."
                                placement="top"
                                arrow
                            >
                                <img src={Images.toolInfoIcon} alt="info" className="section-settings-info-icon" />
                            </SimpleTooltip>
                        </div>
                    </div>
                    <label className="toggle-switch">
                        <input
                            type="checkbox"
                            checked={isContinuousSections(formList?.formSettings)}
                            onChange={(e) => handleFormSettingsChange('continuousSections', e.target.checked)}
                        />
                        <span className="slider"></span>
                    </label>
                </div>
                <p className="form-settings-lock-desc">
                    {isContinuousSections(formList?.formSettings)
                        ? 'Sections continue one after another on the same page, in the form and in the PDF.'
                        : 'Every section starts on a new page, in the form and in the PDF.'}
                </p>
            </div>
            <div className="properties-divider" />

            <div className="form-settings-section">
                <div className="form-settings-section-header">
                    <img src={Images.contentIcon} alt="content" style={{ width: 14, height: 14 }} />
                    <span>CONTENT</span>
                </div>

                <div className="form-settings-field">
                    <label className="form-settings-field-label">Template Name</label>
                    <input
                        type="text"
                        className="form-settings-field-input"
                        placeholder="Enter template name"
                        value={formList?.title || ''}
                        onChange={(e) => handleChange('title', e.target.value)}
                    />
                </div>

                <div className="form-settings-field">
                    <label className="form-settings-field-label">Description</label>
                    <textarea
                        className="form-settings-field-textarea"
                        placeholder="Enter description"
                        value={formList?.description || ''}
                        onChange={(e) => handleChange('description', e.target.value)}
                        rows={3}
                    />
                </div>

                <div className="properties-divider" />
            </div>
        </div>
    );
};
